import { Progress as BaseProgress } from '@base-ui/react/progress';
import type { ComponentProps, ReactNode } from 'react';
import { cx } from '../lib/styled.tsx';

export interface ProgressProps extends ComponentProps<typeof BaseProgress.Root> {
  label?: ReactNode;
  /** Show the formatted value opposite the label. */
  showValue?: boolean;
}

/**
 * Task completion — a download, an upload, a multi-step form. Pass `value={null}`
 * for work of unknown length; the bar then reports itself as indeterminate.
 */
export function Progress({ label, showValue, className, ...props }: ProgressProps) {
  return (
    <BaseProgress.Root className={cx('flex w-full flex-col gap-2', className)} {...props}>
      {label || showValue ? (
        <div className="flex items-baseline justify-between gap-4">
          {label ? (
            <BaseProgress.Label className="text-sm font-medium text-ink">
              {label}
            </BaseProgress.Label>
          ) : null}
          {showValue ? (
            <BaseProgress.Value className="text-xs tabular-nums text-ink-muted" />
          ) : null}
        </div>
      ) : null}
      <BaseProgress.Track className="h-2 w-full overflow-hidden rounded-full bg-surface-muted">
        <BaseProgress.Indicator
          className={cx(
            'h-full rounded-full bg-brand-400 transition-[width] duration-300 ease-out-quint',
            'data-complete:bg-success',
            'data-indeterminate:w-1/3 data-indeterminate:animate-pulse',
          )}
        />
      </BaseProgress.Track>
    </BaseProgress.Root>
  );
}
